// Photorealistic Earth scene for landing & dashboard.
// Loads textures imperatively (no Suspense) to be robust to bundler quirks.
import { useRef, useMemo, useEffect, useState, createElement as h } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";

const TEX_DAY = "https://threejs.org/examples/textures/planets/earth_atmos_2048.jpg";
const TEX_CLOUDS = "https://threejs.org/examples/textures/planets/earth_clouds_1024.png";
const TEX_NORMAL = "https://threejs.org/examples/textures/planets/earth_normal_2048.jpg";
const TEX_SPEC = "https://threejs.org/examples/textures/planets/earth_specular_2048.jpg";

function useEarthTextures() {
  const [tex, setTex] = useState(null);
  useEffect(() => {
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");
    let cancelled = false;
    const load = (url) => new Promise((res) => loader.load(url, (t) => res(t), undefined, () => res(null)));
    Promise.all([load(TEX_DAY), load(TEX_CLOUDS), load(TEX_NORMAL), load(TEX_SPEC)]).then((arr) => {
      if (cancelled) return;
      arr.forEach((t) => { if (t) { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; } });
      setTex({ day: arr[0], clouds: arr[1], normal: arr[2], spec: arr[3] });
    });
    return () => { cancelled = true; };
  }, []);
  return tex;
}

function PhotoEarth({ size = 1.55, autoRotate = true, tilt = 0.41 }) {
  const ref = useRef();
  const cloudsRef = useRef();
  const atmRef = useRef();
  const tex = useEarthTextures();

  useFrame((_, dt) => {
    if (autoRotate && ref.current) ref.current.rotation.y += dt * 0.05;
    if (cloudsRef.current) cloudsRef.current.rotation.y += dt * 0.06;
    if (atmRef.current) atmRef.current.rotation.y += dt * 0.02;
  });

  // Always render a fallback wireframe earth so something is visible
  const surface = tex && tex.day
    ? h("meshPhongMaterial", {
        map: tex.day,
        normalMap: tex.normal || null,
        specularMap: tex.spec || null,
        shininess: 18,
        emissive: new THREE.Color("#001428"),
        emissiveIntensity: 0.2,
      })
    : h("meshStandardMaterial", {
        color: "#0a2a44", emissive: "#0d4d6e", emissiveIntensity: 0.4, wireframe: false,
      });

  return h(
    "group",
    { rotation: [tilt, 0, 0] },
    h("mesh", { ref }, h("sphereGeometry", { args: [size, 64, 64] }), surface),
    tex && tex.clouds
      ? h("mesh", { ref: cloudsRef },
          h("sphereGeometry", { args: [size * 1.012, 64, 64] }),
          h("meshLambertMaterial", { map: tex.clouds, transparent: true, opacity: 0.55, depthWrite: false })
        )
      : null,
    h("mesh", { ref: atmRef, scale: [1.06, 1.06, 1.06] },
      h("sphereGeometry", { args: [size, 48, 48] }),
      h("meshBasicMaterial", {
        color: "#3FAEFF", transparent: true, opacity: 0.10,
        side: THREE.BackSide, blending: THREE.AdditiveBlending, depthWrite: false,
      })
    ),
    h("mesh", { scale: [1.16, 1.16, 1.16] },
      h("sphereGeometry", { args: [size, 32, 32] }),
      h("meshBasicMaterial", {
        color: "#5FB7FF", transparent: true, opacity: 0.04,
        side: THREE.BackSide, blending: THREE.AdditiveBlending, depthWrite: false,
      })
    )
  );
}

function StarField({ count = 4000 }) {
  const geo = useMemo(() => {
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 30 + Math.random() * 50;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    return g;
  }, [count]);
  return h(
    "points",
    { geometry: geo },
    h("pointsMaterial", { size: 0.06, color: "#ffffff", sizeAttenuation: true, transparent: true, opacity: 0.85 })
  );
}

function Satellite({ radius, speed, tilt, phase = 0, color = "#00D4FF" }) {
  const ref = useRef();
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime() * speed + phase;
    if (ref.current) {
      ref.current.position.set(
        Math.cos(t) * radius,
        Math.sin(t) * Math.sin(tilt) * radius * 0.25,
        Math.sin(t) * radius
      );
    }
  });
  return h(
    "mesh",
    { ref },
    h("boxGeometry", { args: [0.08, 0.08, 0.18] }),
    h("meshStandardMaterial", { color, emissive: color, emissiveIntensity: 1.6, metalness: 0.4 })
  );
}

function OrbitRing({ radius, tilt, opacity = 0.18 }) {
  const obj = useMemo(() => {
    const points = [];
    for (let i = 0; i <= 256; i++) {
      const a = (i / 256) * Math.PI * 2;
      points.push(
        new THREE.Vector3(
          Math.cos(a) * radius,
          Math.sin(a) * Math.sin(tilt) * radius * 0.25,
          Math.sin(a) * radius
        )
      );
    }
    const g = new THREE.BufferGeometry().setFromPoints(points);
    const m = new THREE.LineBasicMaterial({ color: 0x00d4ff, transparent: true, opacity });
    return new THREE.LineLoop(g, m);
  }, [radius, tilt, opacity]);
  return h("primitive", { object: obj });
}

function Scene({ interactive }) {
  return h(
    "group",
    null,
    h("color", { attach: "background", args: ["#03040B"] }),
    h("ambientLight", { intensity: 0.5 }),
    h("directionalLight", { position: [5, 3, 5], intensity: 1.6, color: "#FFE9C8" }),
    h("pointLight", { position: [-6, -2, -4], intensity: 0.5, color: "#3FAEFF" }),
    h(StarField, { count: 4000 }),
    h(PhotoEarth, { size: 1.55 }),
    h(OrbitRing, { radius: 2.6, tilt: 0.2 }),
    h(OrbitRing, { radius: 3.2, tilt: 0.8, opacity: 0.12 }),
    h(OrbitRing, { radius: 3.9, tilt: 1.4, opacity: 0.1 }),
    h(Satellite, { radius: 2.6, speed: 0.45, tilt: 0.2, phase: 0 }),
    h(Satellite, { radius: 3.2, speed: 0.32, tilt: 0.8, phase: 1.2, color: "#66E9FF" }),
    h(Satellite, { radius: 3.9, speed: 0.22, tilt: 1.4, phase: 2.4, color: "#A0D8FF" }),
    interactive
      ? h(OrbitControls, {
          enablePan: false, enableZoom: true, autoRotate: false,
          rotateSpeed: 0.5, minDistance: 3, maxDistance: 12,
        })
      : null
  );
}

export default function Earth3D({ className = "", interactive = false }) {
  return (
    <div className={className} style={{ position: "absolute", inset: 0 }}>
      <Canvas camera={{ position: [0, 1.2, 5.2], fov: 50 }} dpr={[1, 2]}>
        <Scene interactive={interactive} />
      </Canvas>
    </div>
  );
}
